import { capitalize } from "@material-ui/core";
import React, { useCallback, useEffect, useState } from "react";
import { Modal, Tabs, Tab } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { isEmpty, debounce } from "lodash";
import PacmanLoading from "../../../components/widgets/PacmanLoading/PacmanLoading";
import moment from "moment";
import { InvitesDataTable } from "./InvitesDataTable";
// import { DB_DATE_FORMAT, DB_HOURS_FORMAT } from "../../../constants";
export const InventoryModal = ({ event, show, api, handleError, onHide }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const [activeTab, setActiveTab] = useState("orders");
  const [inviteActiveTab, setInviteActiveTab] = useState("all");

  const getData = useCallback(
    async (page, rows, search = "") => {
      if (!isEmpty(event)) {
        try {
          setIsLoading(true);
          api &&
            api
              .get(
                `/store/${event.store._id}/event/${
                  event._id
                }/orders?page=${page}&limit=${rows || limit}&search=${search}`,
              )
              .then(({ data }) => {
                if (data) {
                  const { orders } = data;
                  const { docs, totalDocs, limit, page } = orders;
                  setData(docs);
                  setLimit(limit);
                  setPage(page);
                  setTotalRows(totalDocs);
                  setIsLoading(false);
                }
              });
        } catch (err) {
          setIsLoading(false);
          handleError(err);
        }
      }
    },
    [api, event, handleError, limit],
  );

  // Placeholder for invites data
  const [invitesData, setInvitesData] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);

  const getInvites = useCallback(async () => {
    if (!isEmpty(event)) {
      try {
        setInvitesLoading(true);
        // Replace with your actual API endpoint for invites
        api &&
          api
            .get(
              `/store/${event.store._id}/complimentary_tickets?event_id=${event._id}&status=${inviteActiveTab}`,
            )
            .then(({ data }) => {
              const guestListMap = data.map((order) => {
                const {
                  guest_customer: {
                    first_name,
                    last_name,
                    email,
                    contact_number,
                  },
                  order_details,
                  invite_status,
                } = order;
                const [details] = order_details;
                if (!details) return null;
                let ticketType = "";
                if (details?.item) {
                  ticketType = details.item.name;
                }
                return {
                  _id: order._id,
                  date: order.order_date,
                  time: order.order_time,
                  firstName: first_name,
                  lastName: last_name,
                  email,
                  contact: contact_number,
                  ticketType,
                  quantity: details.qty,
                  invite_status,
                };
              });

              setInvitesData(guestListMap || []);
              setInvitesLoading(false);
            });
      } catch (err) {
        setInvitesLoading(false);
        handleError(err);
      }
    }
  }, [api, event, handleError, inviteActiveTab]);

  useEffect(() => {
    if (activeTab === "orders") {
      getData();
    } else if (activeTab === "invites") {
      getInvites();
    }
    // eslint-disable-next-line
  }, [getData, getInvites, activeTab, inviteActiveTab]);

  const handleSearchInputChange = debounce(async (value) => {
    await getData(1, 10, value);
  }, 1000);

  return (
    <Modal show={show} centered onHide={onHide} size="xl">
      <Modal.Header>
        <Modal.Title>Event Tickets</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tabs
          id="inventory-modal-tabs"
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3"
        >
          <Tab eventKey="orders" title="Tickets">
            <DataTable
              columns={[
                {
                  name: "Booking Number",
                  sortable: true,
                  selector: (row) => {
                    return `#${row.transaction_number}`;
                  },
                },
                {
                  name: "Booked Date",
                  sortable: true,
                  selector: (row) => {
                    return moment(row.order_date).format("MMM D, YYYY");
                  },
                },
                {
                  name: "Timeslot",
                  sortable: true,
                  selector: (row) => {
                    return moment(row.order_time, "HH:mm").format("hh:mm A");
                  },
                },
                {
                  name: "Guest Info",
                  sortable: true,
                  selector: (row) => {
                    return `${capitalize(
                      row.guest_customer.first_name,
                    )} ${capitalize(row.guest_customer.last_name)}`;
                  },
                },
                {
                  name: "Status",
                  center: true,
                  selector: (row) => {
                    return capitalize(row.status);
                  },
                  format: (row) => {
                    return (
                      <span
                        className={`badge badge-pill text-capitalize badge-${
                          row.status === "new" ? "light" : "primary"
                        }`}
                      >
                        {row.status === "completed" ? "Redeemed" : "Pending"}
                      </span>
                    );
                  },
                  sortable: true,
                },
              ]}
              data={data}
              pagination
              persistTableHead
              paginationServer
              progressPending={isLoading}
              paginationTotalRows={totalRows}
              paginationPerPage={limit}
              paginationDefaultPage={page}
              onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {
                getData(currentPage, currentRowsPerPage);
              }}
              onChangePage={(page) => {
                getData(page);
              }}
              progressComponent={
                <div className="d-flex justify-content-center align-items-center h-100">
                  <PacmanLoading removeBackgroundColor />
                </div>
              }
              noHeader
              subHeader
              subHeaderComponent={
                <div className="input-group">
                  <div className="input-group-prepend">
                    <span className="input-group-text">
                      <i className="flaticon-search"></i>
                    </span>
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Booking Number or Guest Last Name"
                    onChange={(e) => {
                      handleSearchInputChange(e.target.value);
                    }}
                  />
                </div>
              }
            />
          </Tab>
          <Tab eventKey="invites" title="Invites">
            <Tabs
              id="invites-tabs"
              activeKey={inviteActiveTab}
              onSelect={(key) => setInviteActiveTab(key)}
              className="mb-2"
            >
              <Tab eventKey="all" title="All">
                {invitesLoading ? (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <PacmanLoading removeBackgroundColor />
                  </div>
                ) : (
                  <DataTable
                    columns={[
                      {
                        name: "Name",
                        selector: (guest) =>
                          (guest.firstName ? guest.firstName : "") +
                            (guest.lastName ? " " + guest.lastName : "") || "-",
                        center: true,
                      },
                      {
                        name: "Type",
                        selector: (guest) => guest.ticketType || "-",
                        center: true,
                      },
                      {
                        name: "Date",
                        selector: (guest) =>
                          moment(guest.date).format("MMM D, YYYY") || "-",
                        center: true,
                      },
                      {
                        name: "Time",
                        selector: (guest) =>
                          moment(guest.time, "HH:mm").format("hh:mm A") || "-",
                        center: true,
                        width: "70px",
                        style: {
                          textAlign: "center",
                          padding: "0 2px", // reduces padding to make column compact
                        },
                      },

                      {
                        name: "Quantity",
                        selector: (guest) => guest.quantity || "-",
                        center: true,
                        width: "70px",
                        style: {
                          textAlign: "center",
                          padding: "0 2px", // reduces padding to make column compact
                        },
                      },
                      {
                        name: "Email",
                        selector: (guest) => guest.email || "-",
                        center: true,
                      },
                      {
                        name: "Contact",
                        selector: (guest) => guest.contact || "-",
                        center: true,
                      },
                      {
                        name: "Status",
                        cell: (guest) =>
                          guest.invite_status === "confirmed" ? (
                            <i
                              className="fas fa-check-circle text-success"
                              title="Confirmed"
                              style={{ fontSize: "1.2em" }}
                            />
                          ) : guest.invite_status === "pending" ? (
                            <i
                              className="fas fa-clock text-warning"
                              title="Pending"
                              style={{ fontSize: "1.2em" }}
                            />
                          ) : guest.invite_status === "rejected" ? (
                            <i
                              className="fas fa-times-circle text-danger"
                              title="Rejected"
                              style={{ fontSize: "1.2em" }}
                            />
                          ) : (
                            <span className="text-muted">-</span>
                          ),
                        center: true,
                      },
                    ]}
                    data={invitesData}
                    noHeader
                    persistTableHead
                    pagination
                  />
                )}
              </Tab>
              <Tab eventKey="confirmed" title="Attending">
                {invitesLoading ? (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <PacmanLoading removeBackgroundColor />
                  </div>
                ) : (
                  <InvitesDataTable invitesData={invitesData} />
                )}
              </Tab>
              <Tab eventKey="pending" title="Pending">
                {invitesLoading ? (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <PacmanLoading removeBackgroundColor />
                  </div>
                ) : (
                  <InvitesDataTable invitesData={invitesData} />
                )}
              </Tab>
              <Tab eventKey="rejected" title="Rejected">
                {invitesLoading ? (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <PacmanLoading removeBackgroundColor />
                  </div>
                ) : (
                  <InvitesDataTable invitesData={invitesData} />
                )}
              </Tab>
            </Tabs>
          </Tab>
        </Tabs>
      </Modal.Body>
    </Modal>
  );
};
